import { useState, useCallback } from 'react';
import { catalogUsagesService, CatalogType } from '../../services/catalogUsagesService';
import { toastError, toastSuccess } from '../../lib/toast';

interface UsagesModalState {
  open: boolean;
  objectId: string;
  objectLabel: string;
}

export function useCatalogUsages(catalogType: CatalogType) {
  const [modal, setModal] = useState<UsagesModalState>({
    open: false,
    objectId: '',
    objectLabel: '',
  });
  const [counts, setCounts] = useState<Record<string, number>>({});

  const openModal = useCallback((objectId: string, objectLabel: string) => {
    setModal({ open: true, objectId, objectLabel });
  }, []);

  const closeModal = useCallback(() => {
    setModal((m) => ({ ...m, open: false }));
  }, []);

  const fetchCount = useCallback(
    async (objectId: string): Promise<number> => {
      try {
        const res = await catalogUsagesService.getUsages(catalogType, objectId);
        setCounts((prev) => ({ ...prev, [objectId]: res.count }));
        return res.count;
      } catch {
        return 0;
      }
    },
    [catalogType]
  );

  const handleDeleteWithGuard = useCallback(
    async (
      objectId: string,
      objectLabel: string,
      doDelete: () => Promise<void>,
      onSuccess: () => void
    ) => {
      try {
        await doDelete();
        toastSuccess('Supprimé');
        onSuccess();
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 409 && detail && typeof detail === 'object' && detail.detail === 'object_referenced') {
          const usagesArr = Array.isArray(detail.usages) ? detail.usages : [];
          toastError(
            `Cet objet est utilisé par ${usagesArr.length} couple(s) client/domaine. Désassignez-le avant suppression.`
          );
          openModal(objectId, objectLabel);
        } else {
          toastError(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
        }
      }
    },
    [openModal]
  );

  return {
    modal,
    counts,
    openModal,
    closeModal,
    fetchCount,
    handleDeleteWithGuard,
  };
}
